/**
 * The anonymous surfaces' read model: a public projection → the one record the page renders.
 *
 * `/c/:code` and `/claim/:token` are the two screens a visitor and a recipient ever see, and they
 * are the prototype's screens — the same components the visual acceptance measures. Those
 * components read a `DemoCredential` out of the store, so the job here is to build exactly one
 * from what the anonymous API is allowed to say, and nothing more.
 *
 * Three things this is careful about.
 *
 * It does not enlarge what the server released. Every field below comes from the response: the
 * public claims the release marked public, the labels that release gave them, the release's own
 * localized text and the issuer as the projection named it. Nothing is inferred from another
 * record and nothing is read from the template as it stands today (rules 3 and 4).
 *
 * It does not draw the credential. `artifactSvg` carries the STORED artifact — the bytes the
 * render manifest was hashed over — and `credentialArtwork` prefers it over the client renderer
 * (rule 10). The synthetic release below therefore has no layers, no canvas and no fonts: they
 * would be a second, unauthoritative source for the same picture.
 *
 * It does not decide the claim gate. The server already applied it; the mapping below turns that
 * verdict into the state and token facts `claimGate` reads, so the page and the command still
 * answer for the same reason — which is the property `claim-flow.ts` exists to protect.
 */
import type { ActivePublicView, ClaimPreview, PendingPublicView, PublicView } from '../../api/types';
import type { LinkedInIssuingOrganization } from '../lib/linkedin-issuing-organization';
import { emptyDatabase } from './empty-db';
import type {
  DemoCredential,
  DemoDatabase,
  DemoFieldDefinition,
  DemoRelease,
  ServerViewState,
  SubjectType,
} from './types';

/** The synthetic ids the public surface uses. They never leave the tab. */
const PUBLIC_ID = 'public_record';
const PUBLIC_RELEASE_ID = 'public_release';

function publicField(
  key: string,
  label: string,
  labels: { tr?: string; en?: string } | null,
  order: number,
  listed = true,
): DemoFieldDefinition {
  return {
    id: `${PUBLIC_RELEASE_ID}_${key}`,
    key,
    /*
     * `labelOf` prints the language pair when the template stored one and falls back to the raw
     * key otherwise — never an empty `<dt>`. A release that carried only the single backend label
     * is better served by that label in both languages than by its key, so it stands in for both.
     */
    labels: labels ?? (label ? { tr: label, en: label } : null),
    dataType: 'string',
    required: false,
    importable: false,
    /*
     * The server already applied `visibility === 'public' && publicVisible`; a field that failed
     * that test is not in the response at all, and no field it withheld can be conjured here —
     * there is nothing to conjure it from.
     *
     * Among the survivors the projection's `listed` carries the fourth value the product's
     * visibility has always had: a field the author marked «Yalnız görsel» is drawn on the artwork
     * and NOT repeated in the page's definition list. It is `public_render` here for exactly that
     * reason, and `publicPage`'s own filter — the prototype's, unchanged — is what acts on it. The
     * value is still public and still present; a projection that does not report `listed` at all
     * is listed, which is what this page did before the flag existed.
     */
    visibility: listed ? 'public_metadata_and_render' : 'public_render',
    defaultValue: null,
    normalization: [],
    validation: {},
    sample: '',
    auto: false,
    role: null,
    custom: true,
    sortOrder: order,
  } as DemoFieldDefinition;
}

interface ReleaseShape {
  credentialKind: string;
  customTypeLabel: string | null;
  subjectType: SubjectType;
  defaultLanguage: 'tr' | 'en';
  sealedLanguages?: ('tr' | 'en')[];
  localizedMetadata: Record<string, Record<string, string>>;
  issuer: {
    displayName: string;
    linkedinIssuerName: string;
    linkedinPageUrl: string;
    linkedinOrganizationId: string | null;
    website: string;
    publicBase: string;
  };
  permissions: { allowDownload: boolean; allowLinkedInProfile: boolean; allowFeedShare: boolean };
  fields: DemoFieldDefinition[];
  holderField: string;
  hasAlpha: boolean | null;
  /** The optional LinkedIn «Veren kuruluş» the record's own release froze, or null. */
  linkedInIssuer: LinkedInIssuingOrganization | null;
  /** The release stores an organisation the server could not read; never «none». */
  linkedInIssuerUnreadable: boolean;
}

/**
 * The record's own release, as much of it as an anonymous page is entitled to.
 *
 * `layers`, `canvas` and `fontReferences` are deliberately empty. The page renders the stored
 * artifact, so a design here would be a second source for the same drawing — and the layer plan is
 * not public data.
 */
function syntheticRelease(shape: ReleaseShape): DemoRelease {
  return {
    id: PUBLIC_RELEASE_ID,
    templateId: '',
    templateCode: '',
    templateName: '',
    releaseNumber: 1,
    releaseSchemaVersion: '',
    releasedAt: '',
    releaseNote: null,
    releaseHash: '',
    credentialType: shape.credentialKind,
    customTypeLabel: shape.customTypeLabel,
    subjectType: shape.subjectType,
    holderField: shape.holderField,
    defaultLanguage: shape.defaultLanguage,
    /* One entry unless the projection said otherwise: a single-language release reads as it did. */
    sealedLanguages: shape.sealedLanguages ?? [shape.defaultLanguage],
    localizedMetadata: shape.localizedMetadata,
    validityPolicy: { mode: 'none' },
    publicationPolicy: '',
    permissions: {
      allowDownload: shape.permissions.allowDownload,
      allowLinkedInProfile: shape.permissions.allowLinkedInProfile,
      /* the backend's `allowFeedShare`; the prototype calls the same permission `allowLinkedInShare` */
      allowLinkedInShare: shape.permissions.allowFeedShare,
    },
    duplicatePolicy: {
      keyFields: [],
      scope: '',
      blockingStates: [],
      allowRenewalAfterExpiry: false,
      onDuplicate: 'skip',
    },
    emailDefaults: {},
    interoperability: {},
    canvas: { width: 0, height: 0, aspectRatio: 1, orientation: 'square', unit: 'px', outputScale: 1 },
    background: shape.hasAlpha === null ? null : { hasAlpha: shape.hasAlpha },
    fieldDefinitions: shape.fields,
    layers: [],
    safeAreas: [],
    linkedInIssuer: shape.linkedInIssuer,
    linkedInIssuerUnreadable: shape.linkedInIssuerUnreadable,
    renderProfile: {},
    issuerProfileSnapshot: {
      displayName: shape.issuer.displayName,
      linkedinIssuerName: shape.issuer.linkedinIssuerName,
      linkedinPageUrl: shape.issuer.linkedinPageUrl,
      /* Public information, and the only thing that makes LinkedIn preselect the official Page. */
      linkedinOrganizationId: shape.issuer.linkedinOrganizationId,
      website: shape.issuer.website,
      /* The projection carries no issuer e-mail on purpose; the public page never shows one. */
      supportEmail: '',
      publicBase: shape.issuer.publicBase,
    },
    fontReferences: [],
  };
}

function baseCredential(o: {
  publicCode: string;
  canonicalPublicUrl: string;
  release: DemoRelease;
  claims: Record<string, unknown>;
  subjectType: SubjectType;
  holderDisplayName: string;
  lang: string;
  issueDate: string;
  expiresAt: string | null;
  state: DemoCredential['state'];
  /** The server's own verdict, when this projection carried one. */
  serverViewState?: ServerViewState;
  artifactSvg: string | null;
  /** One stored document per sealed language, when the loader fetched more than one. */
  artifactSvgByLocale?: Partial<Record<'tr' | 'en', string | null>>;
}): DemoCredential {
  return {
    id: PUBLIC_ID,
    publicCode: o.publicCode,
    canonicalPublicUrl: o.canonicalPublicUrl,
    templateId: '',
    releaseId: PUBLIC_RELEASE_ID,
    releaseHash: '',
    release: o.release,
    subjectId: '',
    subjectSnapshot: { subjectType: o.subjectType, canonicalDisplayName: o.holderDisplayName },
    claims: o.claims,
    /* The recipient's address is never on an anonymous page and is never fetched to put it there. */
    privateContactEmail: null,
    lang: o.lang,
    issueDate: o.issueDate,
    expiresAt: o.expiresAt,
    state: o.state,
    ...(o.serverViewState === undefined ? {} : { serverViewState: o.serverViewState }),
    publishedAt: null,
    revokedAt: null,
    supersedesCode: null,
    supersededByCode: null,
    renderManifest: {},
    renderManifestHash: '',
    revision: 1,
    revisions: [],
    claimToken: null,
    deliveryStatus: null,
    batchId: null,
    sourceRow: null,
    createdBy: '',
    createdAt: '',
    updatedAt: '',
    events: [],
    ...(o.artifactSvg === null ? {} : { artifactSvg: o.artifactSvg }),
    /*
     * The documents, by language.
     *
     * `artifactSvg` stays exactly what it was — the record's own language — so every existing
     * reader is unaffected. This is beside it, and a language absent from here has no document,
     * which is what the page says rather than showing the other language's.
     */
    ...(o.artifactSvgByLocale === undefined ? {} : { artifactSvgByLocale: o.artifactSvgByLocale }),
  };
}

const HOLDER_KEY = 'holder';

function pendingCredential(view: PendingPublicView): DemoCredential {
  const release = syntheticRelease({
    credentialKind: view.presentation.credentialKind,
    customTypeLabel: view.presentation.customTypeLabel,
    /* A waiting page shows no holder at all, so the subject type only reaches `credTerms`. */
    subjectType: view.credentialType,
    defaultLanguage: 'tr',
    localizedMetadata: {},
    issuer: {
      displayName: view.issuerDisplayName,
      linkedinIssuerName: '',
      linkedinPageUrl: '',
      linkedinOrganizationId: null,
      website: '',
      publicBase: '',
    },
    permissions: { allowDownload: false, allowLinkedInProfile: false, allowFeedShare: false },
    fields: [],
    holderField: HOLDER_KEY,
    hasAlpha: null,
    linkedInIssuer: null,
    linkedInIssuerUnreadable: false,
  });
  return baseCredential({
    publicCode: view.publicCode,
    canonicalPublicUrl: '',
    release,
    /* Rule 12: a waiting record publishes no name, no claims and no artwork. There is nothing to
       withhold here because the projection carried none of it. */
    claims: {},
    subjectType: view.credentialType,
    holderDisplayName: '',
    lang: 'tr',
    issueDate: '',
    expiresAt: null,
    state: view.status === 'pending_admin_publish' ? 'pending_admin_publish' : 'pending_acceptance',
    /* The server said which of the two it is waiting for; nothing here reinterprets that. */
    serverViewState: view.status === 'pending_admin_publish' ? 'pending_admin_publish' : 'pending_acceptance',
    artifactSvg: null,
  });
}

/**
 * The release's localised metadata with the record's own title filled in for ITS OWN LANGUAGE.
 *
 * Existing titles are left exactly as they are: a release that deliberately localised the name
 * keeps its wording. What changed is the reach of the fallback. It used to be applied to every
 * language, so a release with no English name published its Turkish name AS its English name — the
 * substitution the whole TR/EN round exists to stop. `titleSnapshot` is the record's title in the
 * language the record was issued in, and that is the only language it can honestly fill.
 */
function withRecordTitle(
  metadata: Record<string, Record<string, string>>,
  recordTitle: string,
  defaultLanguage: string,
): Record<string, Record<string, string>> {
  if (!recordTitle) return metadata;
  const out: Record<string, Record<string, string>> = {};
  for (const [lang, entry] of Object.entries(metadata)) out[lang] = { ...entry };
  const own = { ...(out[defaultLanguage] ?? {}) };
  if (!own.title) own.title = recordTitle;
  out[defaultLanguage] = own;
  return out;
}

function activeCredential(
  view: ActivePublicView,
  artifactSvg: string | null,
  artifactSvgByLocale?: Partial<Record<'tr' | 'en', string | null>>,
): DemoCredential {
  const p = view.presentation;
  const claims: Record<string, unknown> = {};
  const fields = p.fields.map((f, i) => {
    claims[f.key] = f.value;
    return publicField(f.key, f.label, f.labels, i, f.listed !== false);
  });
  claims[HOLDER_KEY] = view.holderDisplayName ?? '';
  fields.push(publicField(HOLDER_KEY, p.holderLabel ?? '', null, fields.length));
  const release = syntheticRelease({
    credentialKind: p.credentialKind,
    customTypeLabel: p.customTypeLabel,
    subjectType: p.subjectType,
    defaultLanguage: p.defaultLanguage,
    ...(p.sealedLanguages === undefined ? {} : { sealedLanguages: p.sealedLanguages }),
    /*
     * The record's OWN title wins.
     *
     * `view.title` is `Credential.titleSnapshot`: the name frozen onto this certificate when it
     * was issued. The release's localised metadata may carry a description and criteria but has no
     * obligation to carry a title, and when it does not, the page rendered a blank heading and the
     * LinkedIn link had no name to send. Reading the snapshot fixes both, and it is also the only
     * correct source — a title taken from today's template would re-describe an old certificate.
     */
    localizedMetadata: withRecordTitle(
      p.localizedMetadata as Record<string, Record<string, string>>,
      view.title,
      p.defaultLanguage,
    ),
    issuer: p.issuer,
    permissions: view.permissions,
    fields,
    holderField: HOLDER_KEY,
    hasAlpha: p.artworkHasAlpha,
    /* The organisation the record's own release froze for LinkedIn, if the template named one.
       Separate from `issuer`, which stays the issuer of record on the verification page. */
    linkedInIssuer: p.linkedInIssuer ?? null,
    linkedInIssuerUnreadable: p.linkedInIssuerUnreadable === true,
  });
  const c = baseCredential({
    publicCode: view.publicCode,
    canonicalPublicUrl: p.canonicalPublicUrl,
    release,
    claims,
    subjectType: p.subjectType,
    holderDisplayName: view.holderDisplayName ?? '',
    lang: p.defaultLanguage,
    issueDate: view.issueDate ?? '',
    expiresAt: view.expiresAt,
    /*
     * `state` is the STORED state this verdict was reached from, because that is what the rest of
     * the surface asks it for: `expired` is stored `active` with a past expiry (PO-Q13: the record
     * survives its validity) and keeps an expired record's download and share permissions, and
     * `superseded` is stored `revoked` plus a successor.
     *
     * The verdict itself travels separately and wins. Reconstructing the stored state and letting
     * the page derive the rest from it was the bug: deriving needs a today, the only today a
     * browser has is the visitor's, and so a correct `expired` answer from the server could be
     * redrawn as «Aktif» — by a stale constant, a wrong device clock, or a timezone.
     */
    state: view.status === 'expired' ? 'active' : view.status === 'superseded' ? 'revoked' : view.status,
    serverViewState: view.status,
    artifactSvg,
    ...(artifactSvgByLocale === undefined ? {} : { artifactSvgByLocale }),
  });
  c.revokedAt = view.revokedAt;
  c.supersededByCode = view.supersededBy;
  return c;
}

/**
 * One document, or one per sealed language.
 *
 * A string (or null) is what every caller passed before this and still means «the record's own
 * document». A map means the loader fetched each sealed language, and then the page can answer a
 * language switch from what it holds rather than by showing the other language's picture.
 */
export type PublicArtifacts = string | null | Partial<Record<'tr' | 'en', string | null>>;

export function publicCredentialFromApi(view: PublicView, artifacts: PublicArtifacts): DemoCredential {
  if (view.kind === 'pending') return pendingCredential(view);
  if (typeof artifacts === 'string' || artifacts === null) return activeCredential(view, artifacts);
  /* The record's OWN language, which is `credentials.lang` and not the release's declaration: a
     record issued in the language its release did not declare itself to be in still has its own. */
  const ownLang =
    view.presentation.recordLanguage ?? (view.presentation.defaultLanguage === 'en' ? 'en' : 'tr');
  return activeCredential(view, artifacts[ownLang] ?? null, artifacts);
}

// --------------------------------------------------------------------------------- the claim link

/** What `claimGate` needs to reach the answer the SERVER already chose. */
interface GateFacts {
  state: DemoCredential['state'];
  token: { expiresAt: string | null; consumedAt: string | null; revokedAt: string | null } | null;
}

const PAST = '1970-01-01T00:00:00.000Z';

function gateFactsFor(status: Exclude<ClaimPreview, { status: 'claimable' }>['status']): GateFacts {
  switch (status) {
    case 'credential_revoked':
      return { state: 'revoked', token: null };
    case 'credential_declined':
      /* `claimGate` answers «not awaiting acceptance» for any state but `pending_acceptance`, and
         a declined record is exactly that — the reference's own `wrong_state` branch. */
      return { state: 'declined', token: null };
    case 'token_expired':
      return {
        state: 'pending_acceptance',
        token: { expiresAt: PAST, consumedAt: null, revokedAt: null },
      };
    case 'token_revoked':
      return {
        state: 'pending_acceptance',
        token: { expiresAt: null, consumedAt: null, revokedAt: PAST },
      };
    case 'token_consumed':
      return {
        state: 'pending_acceptance',
        token: { expiresAt: null, consumedAt: PAST, revokedAt: null },
      };
    default:
      return { state: 'pending_acceptance', token: null };
  }
}

/**
 * The record behind a claim link, from the preview the server answered.
 *
 * `already_published` is the one rejection that carries a public code, and it is the one branch
 * the reference offers a button on («Doğrulama sayfamı aç»); the others get the notice alone.
 */
export function claimCredentialFromApi(
  preview: ClaimPreview,
  artifactSvg: string | null,
  /** One stored document per sealed language, when the record has more than one. */
  artifactSvgByLocale?: Partial<Record<'tr' | 'en', string | null>>,
): DemoCredential | null {
  if (preview.status === 'claimable') {
    const c = preview.credential;
    const own: 'tr' | 'en' = c.lang === 'en' ? 'en' : 'tr';
    /*
     * EVERY SEALED LANGUAGE'S OWN TITLE, from the record's immutable release.
     *
     * This used to write `c.title` under the record's own language and nothing else, which was honest
     * about not inventing a translation but left the other tab naming the certificate with an empty
     * string. The preview now carries the titles the release was sealed with, so each language prints
     * the name its author wrote — and the record's own language still prints `titleSnapshot`, which is
     * what the credential itself was issued under and outranks the release's copy of it.
     *
     * Nothing is copied ACROSS languages: a release that sealed no English title leaves the English
     * entry absent, and the screen shows an empty name rather than the Turkish one.
     */
    const titles: Partial<Record<'tr' | 'en', { title: string }>> = {};
    for (const [lang, entry] of Object.entries(c.localizedMetadata ?? {}))
      if ((lang === 'tr' || lang === 'en') && typeof entry?.title === 'string')
        titles[lang] = { title: entry.title };
    titles[own] = { title: c.title };
    const release = syntheticRelease({
      credentialKind: c.credentialKind,
      customTypeLabel: c.customTypeLabel,
      subjectType: c.credentialType,
      /* The release's own declared language when the server says it, and the record's own otherwise —
         which is every claim link made before the preview carried it. */
      defaultLanguage: c.defaultLanguage ?? own,
      ...(c.sealedLanguages === undefined ? {} : { sealedLanguages: c.sealedLanguages }),
      localizedMetadata: titles,
      issuer: {
        displayName: c.issuerDisplayName,
        linkedinIssuerName: '',
        linkedinPageUrl: '',
        linkedinOrganizationId: null,
        website: '',
        publicBase: '',
      },
      permissions: c.permissions,
      fields: [publicField(HOLDER_KEY, '', null, 0)],
      holderField: HOLDER_KEY,
      hasAlpha: null,
      linkedInIssuer: null,
      linkedInIssuerUnreadable: false,
    });
    const record = baseCredential({
      publicCode: c.publicCode,
      canonicalPublicUrl: c.canonicalPublicUrl,
      release,
      claims: { [HOLDER_KEY]: c.holderDisplayName },
      subjectType: c.credentialType,
      holderDisplayName: c.holderDisplayName,
      lang: c.lang,
      issueDate: c.issueDate,
      expiresAt: c.expiresAt,
      state: 'pending_acceptance',
      artifactSvg,
      ...(artifactSvgByLocale === undefined ? {} : { artifactSvgByLocale }),
    });
    record.claimToken = {
      expiresAt: preview.token.expiresAt,
      consumedAt: null,
      revokedAt: null,
      rotationCount: preview.token.rotationCount,
    };
    return record;
  }

  const facts = gateFactsFor(preview.status);
  const release = syntheticRelease({
    credentialKind: 'badge',
    customTypeLabel: null,
    subjectType: 'person',
    defaultLanguage: 'tr',
    localizedMetadata: {},
    issuer: {
      displayName: '',
      linkedinIssuerName: '',
      linkedinPageUrl: '',
      linkedinOrganizationId: null,
      website: '',
      publicBase: '',
    },
    permissions: { allowDownload: false, allowLinkedInProfile: false, allowFeedShare: false },
    fields: [],
    holderField: HOLDER_KEY,
    hasAlpha: null,
    linkedInIssuer: null,
    linkedInIssuerUnreadable: false,
  });
  const record = baseCredential({
    publicCode: preview.status === 'already_published' ? preview.publicCode : '',
    canonicalPublicUrl: preview.status === 'already_published' ? preview.canonicalPublicUrl : '',
    release,
    claims: {},
    subjectType: 'person',
    holderDisplayName: '',
    lang: 'tr',
    issueDate: '',
    expiresAt: null,
    state: preview.status === 'already_published' ? 'active' : facts.state,
    artifactSvg: null,
  });
  if (facts.token) record.claimToken = facts.token;
  return record;
}

/** One record, in a store the prototype's screens can read. */
export function publicDatabase(credential: DemoCredential | null): DemoDatabase {
  const db = emptyDatabase();
  if (credential) {
    db.credentials = [credential];
    db.settings = {
      ...db.settings,
      issuerDisplayName: credential.release.issuerProfileSnapshot.displayName,
      linkedinIssuerName: credential.release.issuerProfileSnapshot.linkedinIssuerName,
      linkedinPageUrl: credential.release.issuerProfileSnapshot.linkedinPageUrl,
      linkedinOrganizationId: credential.release.issuerProfileSnapshot.linkedinOrganizationId ?? null,
      issuerWebsite: credential.release.issuerProfileSnapshot.website,
      publicBase: credential.release.issuerProfileSnapshot.publicBase,
    };
  }
  return db;
}
